import { ArrowUpRight } from "lucide-react";

/**
 * 📌 CARA MENAMBAHKAN / MENGEDIT PROYEK ANDA
 * --------------------------------------------------------------
 * Setiap proyek memiliki field berikut:
 *   idx       : nomor urut (string), mis. "01", "02"
 *   category  : label kategori kecil di atas (mis. "F&B · Cafe")
 *   title     : judul proyek yang ditampilkan
 *   desc      : deskripsi singkat 1–2 kalimat
 *   url       : link website klien anda yang asli
 *               (mis. "https://showroomjayapura.netlify.app")
 *
 * Gambar di-generate otomatis dari URL:
 *   • Desktop  → screenshot tampilan desktop (landscape)
 *   • Mobile   → screenshot tampilan mobile (portrait) — agar
 *                pengunjung dari HP lihat preview asli mobile-nya.
 * --------------------------------------------------------------
 */

const enc = (u) => encodeURIComponent(u);
const desktopShot = (url) =>
    `https://s.wordpress.com/mshots/v1/${enc(url)}?w=1400&h=900`;
const mobileShot = (url) =>
    `https://s.wordpress.com/mshots/v1/${enc(url)}?w=540&h=1100&vpw=390&vph=820`;

const projects = [
    {
        idx: "01",
        category: "Tour & Travel · Haji Umroh",
        title: "Tour dan Travel",
        desc: "Halaman paket perjalanan lengkap dengan jadwal keberangkatan, fasilitas, dan formulir pendaftaran yang mengarah langsung ke admin.",
        url: "https://tourdantravel.netlify.app",
    },
    {
        idx: "02",
        category: "F&B · Restaurant & Cafe",
        title: "Restaurant / Cafe Kamu",
        desc: "Menu digital dengan visual menggugah, jam operasional, lokasi, dan integrasi reservasi via WhatsApp untuk pelanggan setia.",
        url: "https://cafekamu.netlify.app",
    },
    {
        idx: "03",
        category: "Otomotif · Showroom",
        title: "Showroom Jayapura",
        desc: "Katalog mobil bekas dengan halaman detail kendaraan, foto galeri, dan informasi kontak penjual yang langsung terhubung ke calon pembeli.",
        url: "https://showroomjayapura.netlify.app",
    },
];

export const Portfolio = () => {
    return (
        <section
            id="portofolio"
            data-testid="portfolio-section"
            className="py-20 md:py-32 bg-jws-surface2"
        >
            <div className="max-w-7xl mx-auto px-5 md:px-10">
                <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16 md:mb-20">
                    <div>
                        <div className="text-xs uppercase tracking-[0.3em] text-jws-muted mb-4">
                            (02) Portofolio
                        </div>
                        <h2 className="font-display text-4xl md:text-6xl tracking-tight leading-[1] max-w-3xl">
                            Karya yang sudah
                            <br />
                            <span className="italic text-jws-primary">
                                membantu
                            </span>{" "}
                            pelaku usaha.
                        </h2>
                    </div>
                    <p className="text-jws-muted max-w-sm">
                        Tiga proyek dari sektor berbeda — klik kartu untuk
                        membuka website-nya di tab baru.
                    </p>
                </div>

                {/* Editorial spread */}
                <div className="grid grid-cols-1 md:grid-cols-12 gap-14 md:gap-14">
                    <div className="col-span-1 md:col-span-10">
                        <ProjectCard p={projects[0]} />
                    </div>
                    <div className="col-span-1 md:col-span-10 md:col-start-3">
                        <ProjectCard p={projects[1]} />
                    </div>
                    <div className="col-span-1 md:col-span-10">
                        <ProjectCard p={projects[2]} />
                    </div>
                </div>
            </div>
        </section>
    );
};

const ProjectCard = ({ p }) => {
    const hasUrl = Boolean(p.url && p.url.trim());
    const Wrapper = hasUrl ? "a" : "div";
    const wrapperProps = hasUrl
        ? {
              href: p.url,
              target: "_blank",
              rel: "noreferrer",
              "data-testid": `portfolio-${p.idx}`,
              className: "group block cursor-pointer",
          }
        : {
              "data-testid": `portfolio-${p.idx}`,
              className: "group block",
          };

    const imgClass =
        "absolute inset-0 h-full w-full object-cover object-top transition-transform duration-[1200ms] group-hover:scale-[1.04]";

    return (
        <Wrapper {...wrapperProps}>
            {/* MOBILE: phone-shaped preview frame */}
            <div className="md:hidden relative w-full mx-auto max-w-[320px]">
                <div className="relative aspect-[9/19] rounded-[2rem] overflow-hidden bg-jws-text p-[10px] shadow-2xl">
                    <div className="absolute top-3 left-1/2 -translate-x-1/2 z-10 h-1.5 w-16 bg-jws-bg/30 rounded-full" />
                    <div className="relative h-full w-full rounded-[1.5rem] overflow-hidden bg-jws-bg">
                        <img
                            src={mobileShot(p.url)}
                            alt={p.title}
                            loading="lazy"
                            className={imgClass}
                        />
                    </div>
                </div>
                {/* category pill */}
                <div className="absolute -top-3 left-4 inline-flex items-center gap-2 bg-jws-bg/95 backdrop-blur px-3 py-1 rounded-full text-xs border border-jws-border">
                    <span className="text-jws-primary font-medium">
                        {p.idx}
                    </span>
                    <span className="h-3 w-px bg-jws-border" />
                    <span>{p.category}</span>
                </div>
            </div>

            {/* DESKTOP: landscape preview */}
            <div className="hidden md:block">
                <div className="relative w-full aspect-[16/10] overflow-hidden rounded-2xl bg-jws-bg">
                    <img
                        src={desktopShot(p.url)}
                        alt={p.title}
                        loading="lazy"
                        className={imgClass}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-jws-text/40 via-transparent to-transparent" />
                    <div className="absolute top-4 left-4 inline-flex items-center gap-2 bg-jws-bg/90 backdrop-blur px-3 py-1 rounded-full text-xs">
                        <span className="text-jws-primary font-medium">
                            {p.idx}
                        </span>
                        <span className="h-3 w-px bg-jws-border" />
                        <span>{p.category}</span>
                    </div>
                    {hasUrl && (
                        <div className="absolute bottom-4 right-4 h-12 w-12 grid place-items-center rounded-full bg-jws-bg text-jws-text translate-y-2 opacity-0 group-hover:translate-y-0 group-hover:opacity-100 transition-all">
                            <ArrowUpRight className="h-5 w-5" />
                        </div>
                    )}
                </div>
            </div>

            <div className="mt-6 md:mt-5 flex items-start justify-between gap-6">
                <div className="w-full">
                    <h3 className="font-display text-2xl md:text-3xl tracking-tight">
                        {p.title}
                    </h3>
                    <p className="mt-2 text-sm md:text-base text-jws-muted leading-relaxed max-w-md">
                        {p.desc}
                    </p>
                    {hasUrl && (
                        <span className="mt-3 inline-flex items-center gap-1.5 text-sm text-jws-primary font-medium">
                            Kunjungi website
                            <ArrowUpRight className="h-4 w-4" />
                        </span>
                    )}
                </div>
            </div>
        </Wrapper>
    );
};
